import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  IconButton,
  LinearProgress,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import { useTranslation } from 'react-i18next';
import { ZERO_ICONS } from '../../constants/zeroIcons';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import { useNow, useRouteRuns, useTruckTracking } from '../../hooks/useOperationsData';
import { contentBorders } from '../../theme/createContentTheme';
import type { OpsRouteRun, RouteRunStatus, TruckTrackingItem } from '../../types/fleet.type';
import {
  dayTotals,
  facilityPoint,
  runLine,
  runProgress,
  runTone,
  sortRuns,
  sortStops,
  stopName,
  stopPoint,
  stopTone,
  truckFreshness,
  truckTone,
} from '../../utils/fleet.helpers';
import { toLngLat, type LngLat } from '../../utils/geo';
import { formatClock, formatDay, formatRelative } from '../../utils/numberFormat';
import { dateFromKey, localDateKey, parseServerTimestamp, shiftDateKey } from '../../utils/serverTime';
import EmptyState from '../EmptyState';
import KpiFigure from '../metrics/KpiFigure';
import MetricStrip from '../metrics/MetricStrip';
import SectionHeader from '../SectionHeader';
import { LazyZeroMap, type MapLine, type MapMarker } from '../map';
import CollectionFlow from './CollectionFlow';
import CommandSurface from './CommandSurface';
import FreshnessLabel from './FreshnessLabel';
import StatusChip from './StatusChip';

type StatusFilter = 'all' | RouteRunStatus;
type Selection = { kind: 'run' | 'truck'; id: string } | null;

const RUN_STATUSES: RouteRunStatus[] = ['in_progress', 'pending', 'completed', 'cancelled'];

interface FleetCommandSurfaceProps {
  orgId: string;
  canRuns: boolean;
  canTrucks: boolean;
}

/**
 * Fleet command surface for Waste Mover organisations: the day's route runs
 * with their TomTom-planned lines and stop progress, plus each truck's last
 * reported position. Everything shown comes from `/ops/route-runs` and
 * `/truck/tracking`; nothing is interpolated or simulated.
 */
const FleetCommandSurface: React.FC<FleetCommandSurfaceProps> = ({ orgId, canRuns, canTrucks }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const fmt = useNumberFormat();
  const borders = contentBorders(theme);
  const now = useNow();
  const today = localDateKey(new Date());
  const [runDate, setRunDate] = useState(today);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [tab, setTab] = useState<'runs' | 'vehicles'>('runs');
  const [selection, setSelection] = useState<Selection>(null);

  const runsQ = useRouteRuns(orgId, runDate, canRuns);
  const trucksQ = useTruckTracking(orgId, canTrucks);
  const runsVisible = canRuns && runsQ.status !== 'forbidden';
  const trucksVisible = canTrucks && trucksQ.status !== 'forbidden';

  const runs = useMemo(() => sortRuns(runsQ.data), [runsQ.data]);
  const totals = useMemo(() => dayTotals(runs), [runs]);
  const shownRuns = statusFilter === 'all' ? runs : runs.filter((r) => r.status === statusFilter);
  const trucks = trucksQ.data;
  const trucksById = useMemo(() => new Map(trucks.map((item) => [item.truck.id, item])), [trucks]);
  const reporting = trucks.filter(
    (item) => toLngLat(item.truck.latitude, item.truck.longitude) && truckFreshness(item.truck.updated_at, now) !== 'stale',
  ).length;

  const selectedRun = selection?.kind === 'run' ? runs.find((r) => r.id === selection.id) : undefined;
  const selectedTruck = selection?.kind === 'truck' ? trucksById.get(selection.id) : undefined;

  if (!runsVisible && !trucksVisible) return null;

  const runStatusLabel = (s: RouteRunStatus) => t(`status.run.${s}`);
  const truckLabel = (item: TruckTrackingItem) => item.truck.name || t('dashboard.fleet.unnamedTruck');
  const runTitle = (run: OpsRouteRun) => run.route_name || run.route_snapshot?.name || t('dashboard.fleet.unnamedRoute');
  const duration = (seconds: number | null | undefined) => {
    if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) return '—';
    const minutes = Math.round(seconds / 60);
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h > 0
      ? `${fmt.number(h)} ${t('units.hoursShort')} ${fmt.number(m)} ${t('units.minutesShort')}`
      : `${fmt.number(m)} ${t('units.minutesShort')}`;
  };
  const clock = (value: string | null | undefined) => {
    const date = parseServerTimestamp(value);
    return date ? formatClock(date, i18n.language) : '—';
  };
  const lastUpdate = (item: TruckTrackingItem) => {
    const date = parseServerTimestamp(item.truck.updated_at);
    return date ? formatRelative(date, Math.max(now, date.getTime()), i18n.language) : '—';
  };

  // ---- map features ----------------------------------------------------------------------
  const lines: MapLine[] = runsVisible
    ? shownRuns.flatMap((run) => {
        const line = runLine(run);
        if (!line) return [];
        const state = selectedRun ? (selectedRun.id === run.id ? 'selected' : 'dimmed') : 'default';
        return [{ id: `run:${run.id}`, coordinates: line.coordinates, planned: line.planned, tone: runTone(run.status), state }];
      })
    : [];

  const markers: MapMarker[] = [];
  if (trucksVisible) {
    for (const item of trucks) {
      const position = toLngLat(item.truck.latitude, item.truck.longitude);
      if (!position) continue;
      const tone = truckTone(item.truck);
      const related = selectedRun ? selectedRun.truck_id === item.truck.id : selectedTruck ? selectedTruck.truck.id === item.truck.id : true;
      const selected = selectedTruck?.truck.id === item.truck.id || selectedRun?.truck_id === item.truck.id;
      markers.push({
        id: `truck:${item.truck.id}`,
        position,
        tone,
        label: `${truckLabel(item)} · ${t(`status.truck.${item.truck.status}`)}`,
        glyph: 'truck',
        pulse: tone === 'active' && truckFreshness(item.truck.updated_at, now) === 'recent',
        state: selected ? 'selected' : selection && !related ? 'dimmed' : 'default',
        raised: true,
      });
    }
  }
  if (selectedRun) {
    for (const stop of sortStops(selectedRun.stops)) {
      const position = stopPoint(stop);
      if (!position) continue;
      markers.push({
        id: `stop:${stop.id}`,
        position,
        tone: stopTone(stop.status),
        label: `${stop.sequence}. ${stopName(stop)} · ${t(`status.stop.${stop.status}`)}`,
        text: String(stop.sequence),
        size: 'sm',
      });
    }
    const start = facilityPoint(selectedRun.route_snapshot?.start_facility);
    const end = facilityPoint(selectedRun.route_snapshot?.end_facility);
    if (start) {
      markers.push({
        id: `facility:start:${selectedRun.id}`,
        position: start,
        tone: 'complete',
        label: `${t('dashboard.fleet.start')}: ${selectedRun.route_snapshot?.start_facility?.name ?? ''}`,
        glyph: 'flag',
        shape: 'square',
        size: 'sm',
      });
    }
    if (end) {
      markers.push({
        id: `facility:end:${selectedRun.id}`,
        position: end,
        tone: 'complete',
        label: `${t('dashboard.fleet.destination')}: ${selectedRun.route_snapshot?.end_facility?.name ?? ''}`,
        glyph: 'facility',
        shape: 'square',
      });
    }
  } else if (runsVisible) {
    const seen = new Set<string>();
    for (const run of shownRuns) {
      const facility = run.route_snapshot?.end_facility;
      const position = facilityPoint(facility);
      const key = facility?.id ?? (position ? position.join(',') : '');
      if (!position || seen.has(key)) continue;
      seen.add(key);
      markers.push({
        id: `facility:end:${key}`,
        position,
        tone: 'complete',
        label: `${t('dashboard.fleet.destination')}: ${facility?.name ?? ''}`,
        glyph: 'facility',
        shape: 'square',
        state: selectedTruck ? 'dimmed' : 'default',
      });
    }
  }

  let fitTo: LngLat[] | undefined;
  if (selectedRun) {
    fitTo = [
      ...(runLine(selectedRun)?.coordinates ?? []),
      ...selectedRun.stops.map(stopPoint).filter((p): p is LngLat => p !== null),
    ];
  } else if (selectedTruck) {
    const p = toLngLat(selectedTruck.truck.latitude, selectedTruck.truck.longitude);
    fitTo = p ? [p] : undefined;
  }
  const fitKey = `${runDate}|${statusFilter}|${selection?.kind ?? ''}:${selection?.id ?? ''}|${lines.length}|${trucksVisible ? trucks.length : 0}`;

  const onMapSelect = (id: string) => {
    const [kind, raw] = id.split(':');
    if (kind === 'run') setSelection({ kind: 'run', id: raw });
    else if (kind === 'truck') {
      const runOfTruck = runs.find((r) => r.truck_id === raw && r.status === 'in_progress') ?? runs.find((r) => r.truck_id === raw);
      setSelection(runOfTruck && runsVisible ? { kind: 'run', id: runOfTruck.id } : { kind: 'truck', id: raw });
    }
  };

  // ---- toolbar ----------------------------------------------------------------------------
  const dateLabel = formatDay(dateFromKey(runDate) ?? new Date(), i18n.language);
  const toolbar = (
    <>
      {runsVisible ? (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Tooltip title={t('dashboard.fleet.previousDay')}>
            <IconButton
              size="small"
              onClick={() => {
                setRunDate((d) => shiftDateKey(d, -1));
                setSelection(null);
              }}
              aria-label={t('dashboard.fleet.previousDay')}
              sx={{ '[dir="rtl"] & svg': { transform: 'scaleX(-1)' } }}
            >
              <ChevronLeftRounded fontSize="small" />
            </IconButton>
          </Tooltip>
          <TextField
            type="date"
            size="small"
            value={runDate}
            onChange={(e) => {
              if (e.target.value) {
                setRunDate(e.target.value);
                setSelection(null);
              }
            }}
            inputProps={{ max: today, 'aria-label': t('dashboard.fleet.day'), dir: 'ltr' }}
            sx={{ width: 150, '& input': { py: 0.75, fontSize: '0.8125rem' } }}
          />
          <Tooltip title={t('dashboard.fleet.nextDay')}>
            <span>
              <IconButton
                size="small"
                disabled={runDate >= today}
                onClick={() => {
                  setRunDate((d) => shiftDateKey(d, 1));
                  setSelection(null);
                }}
                aria-label={t('dashboard.fleet.nextDay')}
                sx={{ '[dir="rtl"] & svg': { transform: 'scaleX(-1)' } }}
              >
                <ChevronRightRounded fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {runDate !== today ? (
            <Button size="small" color="inherit" onClick={() => setRunDate(today)} sx={{ color: 'text.secondary' }}>
              {t('dashboard.fleet.today')}
            </Button>
          ) : null}
        </Box>
      ) : null}
      {runsVisible ? (
        <>
          <Chip
            size="small"
            label={`${t('common.all')} · ${fmt.number(totals.runs)}`}
            onClick={() => setStatusFilter('all')}
            color={statusFilter === 'all' ? 'primary' : 'default'}
            variant={statusFilter === 'all' ? 'filled' : 'outlined'}
            aria-pressed={statusFilter === 'all'}
            sx={{ flexShrink: 0 }}
          />
          {RUN_STATUSES.filter((s) => totals.byStatus[s] > 0).map((s) => (
            <Chip
              key={s}
              size="small"
              label={`${runStatusLabel(s)} · ${fmt.number(totals.byStatus[s])}`}
              onClick={() => setStatusFilter(s)}
              color={statusFilter === s ? 'primary' : 'default'}
              variant={statusFilter === s ? 'filled' : 'outlined'}
              aria-pressed={statusFilter === s}
              sx={{ flexShrink: 0 }}
            />
          ))}
        </>
      ) : null}
      <Box sx={{ flex: { md: 1 }, minWidth: 8 }} />
      <FreshnessLabel
        fetchedAt={Math.max(runsQ.fetchedAt ?? 0, trucksQ.fetchedAt ?? 0) || null}
        refreshing={runsQ.isFetching || trucksQ.isFetching}
        onRefresh={() => {
          if (runsVisible) runsQ.refetch();
          if (trucksVisible) trucksQ.refetch();
        }}
      />
    </>
  );

  // ---- panel ----------------------------------------------------------------------------------
  const rowSx = (active: boolean) => ({
    width: '100%',
    display: 'block',
    px: 2,
    py: 1.5,
    textAlign: 'start' as const,
    borderBottom: `1px solid ${borders.subtle}`,
    bgcolor: active ? 'action.selected' : 'transparent',
    '&:hover': { bgcolor: active ? 'action.selected' : 'action.hover' },
  });

  const runRows = shownRuns.map((run) => {
    const p = runProgress(run);
    const truck = trucksById.get(run.truck_id);
    return (
      <Box component="li" key={run.id}>
        <ButtonBase onClick={() => setSelection({ kind: 'run', id: run.id })} sx={rowSx(selectedRun?.id === run.id)}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600, minWidth: 0 }}>
              {runTitle(run)}
            </Typography>
            <StatusChip tone={runTone(run.status)} label={runStatusLabel(run.status)} pulse={run.status === 'in_progress'} />
          </Box>
          <Typography variant="caption" color="text.secondary" component="p" noWrap sx={{ mt: 0.25 }}>
            {[run.truck_name || (truck ? truckLabel(truck) : null), run.driver_name].filter(Boolean).join(' · ') ||
              t('status.unassigned')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <LinearProgress
              variant="determinate"
              value={p.total > 0 ? (p.completed / p.total) * 100 : 0}
              sx={{ flex: 1 }}
              aria-label={t('dashboard.fleet.stopsProgress', { completed: p.completed, total: p.total })}
            />
            <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', fontWeight: 600 }}>
              <bdi>
                {fmt.number(p.completed)}/{fmt.number(p.total)}
              </bdi>
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
            {[
              typeof run.planned_distance_km === 'number' ? fmt.withUnit('decimal', run.planned_distance_km) + ` ${t('units.km')}` : null,
              run.planned_duration_seconds ? duration(run.planned_duration_seconds) : null,
              run.clock_in_at ? `${t('dashboard.fleet.clockIn')} ${clock(run.clock_in_at)}` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        </ButtonBase>
      </Box>
    );
  });

  const vehicleRows = [...trucks]
    .sort((a, b) => truckLabel(a).localeCompare(truckLabel(b)))
    .map((item) => {
      const located = toLngLat(item.truck.latitude, item.truck.longitude);
      return (
        <Box component="li" key={item.truck.id}>
          <ButtonBase onClick={() => setSelection({ kind: 'truck', id: item.truck.id })} sx={rowSx(selectedTruck?.truck.id === item.truck.id)}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <ZERO_ICONS.vehicle aria-hidden sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                  {truckLabel(item)}
                </Typography>
              </Box>
              <StatusChip tone={truckTone(item.truck)} label={t(`status.truck.${item.truck.status}`)} />
            </Box>
            <Typography variant="caption" color="text.secondary" component="p" noWrap sx={{ mt: 0.5 }}>
              {[
                item.current_driver?.username ?? t('status.unassigned'),
                located ? t('dashboard.fleet.lastUpdate', { time: lastUpdate(item) }) : t('dashboard.fleet.noPosition'),
              ].join(' · ')}
            </Typography>
          </ButtonBase>
        </Box>
      );
    });

  const activeTab = !runsVisible ? 'vehicles' : !trucksVisible ? 'runs' : tab;
  const listOrEmpty = (items: React.ReactNode[], empty: React.ReactNode) =>
    items.length > 0 ? (
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {items}
      </Box>
    ) : (
      <Box sx={{ p: 2 }}>{empty}</Box>
    );

  const panel = (
    <>
      {runsVisible && trucksVisible ? (
        <Tabs
          value={activeTab}
          onChange={(_, v: 'runs' | 'vehicles') => setTab(v)}
          variant="fullWidth"
          sx={{ borderBottom: `1px solid ${borders.default}`, '& .MuiTab-root': { mx: 0, px: 1.5 } }}
        >
          <Tab value="runs" label={`${t('dashboard.fleet.tabs.runs')} · ${fmt.number(shownRuns.length)}`} />
          <Tab value="vehicles" label={`${t('dashboard.fleet.tabs.vehicles')} · ${fmt.number(trucks.length)}`} />
        </Tabs>
      ) : null}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {activeTab === 'runs' ? (
          runsQ.status === 'loading' ? (
            <LinearProgress aria-label={t('common.loading')} />
          ) : runsQ.status === 'error' ? (
            <Box sx={{ p: 2 }}>
              <Alert severity="error" action={<Button color="inherit" size="small" onClick={runsQ.refetch}>{t('common.retry')}</Button>}>
                {t('dashboard.fleet.runsError')}
              </Alert>
            </Box>
          ) : (
            listOrEmpty(
              runRows,
              <EmptyState
                variant="inline"
                icon={ZERO_ICONS.route}
                title={statusFilter === 'all' ? t('dashboard.fleet.empty.noRuns') : t('dashboard.fleet.empty.noRunsFiltered')}
                description={statusFilter === 'all' ? t('dashboard.fleet.empty.noRunsHint', { day: dateLabel }) : undefined}
                minHeight={200}
              />,
            )
          )
        ) : trucksQ.status === 'loading' ? (
          <LinearProgress aria-label={t('common.loading')} />
        ) : (
          listOrEmpty(
            vehicleRows,
            <EmptyState variant="inline" icon={ZERO_ICONS.vehicle} title={t('dashboard.fleet.empty.noVehicles')} minHeight={200} />,
          )
        )}
      </Box>
    </>
  );

  // ---- detail ----------------------------------------------------------------------------------
  let detail: React.ReactNode = null;
  let detailTitle: React.ReactNode = null;
  const fact = (label: string, value: React.ReactNode) => (
    <Box key={label} sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" component="p">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
        {value}
      </Typography>
    </Box>
  );

  if (selectedRun) {
    const p = runProgress(selectedRun);
    const line = runLine(selectedRun);
    const truck = trucksById.get(selectedRun.truck_id);
    detailTitle = (
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
          {runTitle(selectedRun)}
        </Typography>
        <StatusChip tone={runTone(selectedRun.status)} label={runStatusLabel(selectedRun.status)} pulse={selectedRun.status === 'in_progress'} />
      </Box>
    );
    detail = (
      <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 2.5 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
          {fact(t('dashboard.fleet.truck'), selectedRun.truck_name || (truck ? truckLabel(truck) : '—'))}
          {fact(t('dashboard.fleet.driver'), selectedRun.driver_name || t('status.unassigned'))}
          {fact(t('dashboard.fleet.clockIn'), <bdi>{clock(selectedRun.clock_in_at)}</bdi>)}
          {fact(t('dashboard.fleet.clockOut'), <bdi>{clock(selectedRun.clock_out_at)}</bdi>)}
          {fact(
            t('dashboard.fleet.plannedDistance'),
            <bdi>
              {typeof selectedRun.planned_distance_km === 'number'
                ? `${fmt.number(selectedRun.planned_distance_km, { maximumFractionDigits: 1 })} ${t('units.km')}`
                : '—'}
            </bdi>,
          )}
          {fact(t('dashboard.fleet.plannedDuration'), <bdi>{duration(selectedRun.planned_duration_seconds)}</bdi>)}
          {fact(t('dashboard.fleet.offload'), <bdi>{clock(selectedRun.offload_at)}</bdi>)}
          {fact(
            t('dashboard.fleet.destination'),
            selectedRun.route_snapshot?.end_facility?.name || '—',
          )}
        </Box>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t('dashboard.fleet.stopsProgress', { completed: fmt.number(p.completed), total: fmt.number(p.total) })}
          </Typography>
          <LinearProgress variant="determinate" value={p.total > 0 ? (p.completed / p.total) * 100 : 0} sx={{ mt: 1 }} />
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.75 }}>
            {[
              p.remaining > 0 ? t('dashboard.fleet.remaining', { count: p.remaining }) : null,
              p.cancelled > 0 ? t('dashboard.fleet.cancelledStops', { count: p.cancelled }) : null,
              p.weightKg !== null ? t('dashboard.fleet.weighed', { value: fmt.withUnit('mass_kg', p.weightKg, { scaleMass: true }) }) : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
          {line && !line.planned ? (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
              {t('dashboard.fleet.stopOrderOnly')}
            </Typography>
          ) : null}
        </Box>
        <Table size="small" aria-label={t('dashboard.fleet.stops')}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 32 }}>#</TableCell>
              <TableCell>{t('dashboard.fleet.stop')}</TableCell>
              <TableCell>{t('common.status')}</TableCell>
              <TableCell align="right">{t('units.kg')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortStops(selectedRun.stops).map((stop) => (
              <TableRow key={stop.id}>
                <TableCell sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                  <bdi>{fmt.number(stop.sequence)}</bdi>
                </TableCell>
                <TableCell sx={{ maxWidth: 160 }}>
                  <Typography variant="body2" noWrap title={stopName(stop)}>
                    {stopName(stop)}
                  </Typography>
                  {stop.completed_at || stop.arrived_at ? (
                    <Typography variant="caption" color="text.secondary">
                      <bdi>{clock(stop.completed_at ?? stop.arrived_at)}</bdi>
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>
                  <StatusChip tone={stopTone(stop.status)} label={t(`status.stop.${stop.status}`)} bare />
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <bdi>{typeof stop.weight_kg === 'number' ? fmt.number(stop.weight_kg, { maximumFractionDigits: 1 }) : '—'}</bdi>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  } else if (selectedTruck) {
    const located = toLngLat(selectedTruck.truck.latitude, selectedTruck.truck.longitude);
    const freshness = truckFreshness(selectedTruck.truck.updated_at, now);
    const truckRuns = runs.filter((r) => r.truck_id === selectedTruck.truck.id);
    detailTitle = truckLabel(selectedTruck);
    detail = (
      <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 2.5 }}>
        <StatusChip tone={truckTone(selectedTruck.truck)} label={t(`status.truck.${selectedTruck.truck.status}`)} size="md" />
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
          {fact(t('dashboard.fleet.driver'), selectedTruck.current_driver?.username ?? t('status.unassigned'))}
          {fact(t('dashboard.fleet.capacity'), <bdi>{fmt.withUnit('mass_kg', selectedTruck.truck.capacity, { scaleMass: true })}</bdi>)}
          {fact(
            t('dashboard.fleet.lastUpdateLabel'),
            located ? <bdi>{`${lastUpdate(selectedTruck)} · ${clock(selectedTruck.truck.updated_at)}`}</bdi> : t('dashboard.fleet.noPosition'),
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {located && freshness === 'stale' ? t('dashboard.fleet.stalePosition') : t('dashboard.fleet.positionNote')}
        </Typography>
        {runsVisible && truckRuns.length > 0 ? (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              {t('dashboard.fleet.runsThatDay', { day: dateLabel })}
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1 }}>
              {truckRuns.map((run) => (
                <Box component="li" key={run.id}>
                  <ButtonBase
                    onClick={() => setSelection({ kind: 'run', id: run.id })}
                    sx={{ width: '100%', justifyContent: 'space-between', gap: 1, py: 0.75, textAlign: 'start' }}
                  >
                    <Typography variant="body2" noWrap>
                      {runTitle(run)}
                    </Typography>
                    <StatusChip tone={runTone(run.status)} label={runStatusLabel(run.status)} />
                  </ButtonBase>
                </Box>
              ))}
            </Box>
          </Box>
        ) : null}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 2 }}>
      <SectionHeader
        title={t('dashboard.fleet.title', { day: dateLabel })}
        icon={ZERO_ICONS.vehicle}
        description={t('dashboard.fleet.description')}
      />
      {runsVisible ? (
        <MetricStrip columns={{ xs: 2, sm: 3, lg: trucksVisible ? 5 : 4 }} dense>
          <KpiFigure
            icon={ZERO_ICONS.route}
            label={t('dashboard.fleet.strip.runs')}
            value={fmt.number(totals.runs)}
            size="md"
            context={t('dashboard.fleet.strip.runsContext', {
              active: fmt.number(totals.byStatus.in_progress),
              pending: fmt.number(totals.byStatus.pending),
            })}
          />
          <KpiFigure
            icon={ZERO_ICONS.stop}
            label={t('dashboard.fleet.strip.stops')}
            value={`${fmt.number(totals.stopsCompleted)} / ${fmt.number(totals.stopsTotal)}`}
            size="md"
            context={totals.stopsCancelled > 0 ? t('dashboard.fleet.cancelledStops', { count: totals.stopsCancelled }) : undefined}
          />
          <KpiFigure
            icon={ZERO_ICONS.mass}
            label={t('dashboard.fleet.strip.weighed')}
            value={fmt.metric('mass_kg', totals.weightKg, { scaleMass: true }).text}
            unit={totals.weightKg === null ? undefined : fmt.metric('mass_kg', totals.weightKg, { scaleMass: true }).unit}
            size="md"
          />
          <KpiFigure
            icon={ZERO_ICONS.distance}
            label={t('dashboard.fleet.strip.plannedDistance')}
            value={totals.plannedKm === null ? '—' : fmt.number(totals.plannedKm, { maximumFractionDigits: 0 })}
            unit={totals.plannedKm === null ? undefined : t('units.km')}
            size="md"
          />
          {trucksVisible ? (
            <KpiFigure
              icon={ZERO_ICONS.vehicle}
              label={t('dashboard.fleet.strip.reporting')}
              value={`${fmt.number(reporting)} / ${fmt.number(trucks.length)}`}
              size="md"
              context={t('dashboard.fleet.strip.reportingContext')}
            />
          ) : null}
        </MetricStrip>
      ) : null}
      <CommandSurface
        ariaLabel={t('dashboard.fleet.titleShort')}
        toolbar={toolbar}
        map={
          <LazyZeroMap
            ariaLabel={t('map.aria.fleet')}
            lines={lines}
            markers={markers}
            onSelect={onMapSelect}
            fitKey={fitKey}
            fitTo={fitTo}
            sx={{ position: 'absolute', inset: 0 }}
          />
        }
        panel={panel}
        detail={detail}
        detailTitle={detailTitle}
        onCloseDetail={() => setSelection(null)}
      />
      {runsVisible && totals.runs > 0 ? (
        <SectionHeader
          level="minor"
          component="h3"
          title={`${t('dashboard.flow.dayTitle')} · ${dateLabel}`}
          icon={ZERO_ICONS.flow}
          sx={{ mt: 1, mb: 0 }}
        />
      ) : null}
      {runsVisible && totals.runs > 0 ? (
        <CollectionFlow
          ariaLabel={t('dashboard.flow.dayTitle')}
          stages={[
            {
              key: 'planned',
              icon: ZERO_ICONS.stop,
              label: t('dashboard.flow.plannedStops'),
              value: fmt.number(totals.stopsTotal),
              caption: t('dashboard.flow.runsCount', { count: totals.runs }),
            },
            {
              key: 'collected',
              icon: ZERO_ICONS.completed,
              label: t('dashboard.flow.collected'),
              value: fmt.number(totals.stopsCompleted),
              caption:
                totals.weightKg !== null ? fmt.withUnit('mass_kg', totals.weightKg, { scaleMass: true }) : undefined,
            },
            {
              key: 'road',
              icon: ZERO_ICONS.inTransit,
              label: t('dashboard.flow.onTheRoad'),
              value: fmt.number(totals.byStatus.in_progress),
              caption: t('dashboard.flow.runsInProgress'),
            },
            {
              key: 'delivered',
              icon: ZERO_ICONS.facility,
              label: t('dashboard.flow.delivered'),
              value: fmt.number(totals.delivered),
              caption: t('dashboard.flow.runsOffloaded'),
              emphasis: true,
            },
          ]}
        />
      ) : null}
    </Box>
  );
};

export default FleetCommandSurface;
